import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit, FiEye, FiPackage, FiPlus, FiTrash2, FiX } from 'react-icons/fi';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../utils/api';

const emptyForm = () => ({
  name: '',
  mrp: '',
  rate: '',
  stripSize: '10',
  stripsPerBox: '10',
  gstPercent: '5',
});

const formatSno = (index) => String(index + 1).padStart(2, '0');

const formatMoney = (value) => (Number(value) || 0).toFixed(2);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ProductDB = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.getProducts, {
        headers: authHeaders(),
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      Swal.fire('Error', 'Failed to fetch products', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((row) => String(row.name || '').toLowerCase().includes(q));
  }, [products, search]);

  const openCreate = () => {
    setSelected(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setFormData({
      name: row.name || '',
      mrp: String(row.mrp ?? ''),
      rate: String(row.rate ?? ''),
      stripSize: String(row.stripSize ?? 10),
      stripsPerBox: String(row.stripsPerBox ?? 10),
      gstPercent: String(row.gstPercent ?? 5),
    });
    setShowModal(true);
  };

  const openView = (row) => {
    setViewRow(row);
    setShowViewModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setFormData(emptyForm());
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewRow(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Product name is required', 'error');
      return;
    }
    if (!formData.mrp || Number(formData.mrp) < 0) {
      Swal.fire('Error', 'MRP is required', 'error');
      return;
    }
    if (!formData.rate || Number(formData.rate) < 0) {
      Swal.fire('Error', 'Rate is required', 'error');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      mrp: Number(formData.mrp),
      rate: Number(formData.rate),
      stripSize: Number(formData.stripSize) || 10,
      stripsPerBox: Number(formData.stripsPerBox) || 10,
      gstPercent: Number(formData.gstPercent) || 5,
    };

    setSaving(true);
    try {
      if (selected?._id) {
        await axios.put(API_ENDPOINTS.updateProduct(selected._id), payload, {
          headers: authHeaders(),
        });
        Swal.fire('Success', 'Product updated successfully', 'success');
      } else {
        await axios.post(API_ENDPOINTS.createProduct, payload, {
          headers: authHeaders(),
        });
        Swal.fire('Success', 'Product created successfully', 'success');
      }
      closeModal();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to save product', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This product will be removed from the database',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(API_ENDPOINTS.deleteProduct(id), {
        headers: authHeaders(),
      });
      Swal.fire('Deleted', 'Product deleted successfully', 'success');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      Swal.fire('Error', 'Failed to delete product', 'error');
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-slate-100 via-teal-50/30 to-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
                <FiPackage className="text-teal-600" />
                Product DB
              </h1>
              <p className="text-slate-600 mt-1">
                Manage invoice materials — MRP, rate, strip & box packing
              </p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-[#1a365d] px-4 py-2 text-sm font-semibold text-white hover:bg-[#152c4d] transition-colors"
            >
              <FiPlus />
              Add Product
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="font-bold text-slate-800">Material List</h2>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product..."
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full sm:w-64"
            />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">SNO</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Product Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">MRP</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Rate</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Strip Size</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Strips/Box</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">GST %</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Updated</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-12 text-slate-500">
                        No products found. Click Add Product to create one.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((row, index) => (
                      <tr key={row._id} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">{row.sno || formatSno(index)}</td>
                        <td className="px-4 py-3 font-medium uppercase">{row.name}</td>
                        <td className="px-4 py-3 text-slate-600">₹{formatMoney(row.mrp)}</td>
                        <td className="px-4 py-3 text-slate-600">₹{formatMoney(row.rate)}</td>
                        <td className="px-4 py-3 text-slate-600">{row.stripSize}</td>
                        <td className="px-4 py-3 text-slate-600">{row.stripsPerBox}</td>
                        <td className="px-4 py-3 text-slate-600">{row.gstPercent ?? 5}%</td>
                        <td className="px-4 py-3 text-slate-600">{formatDate(row.updatedAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openView(row)}
                              className="text-blue-600 hover:text-blue-800"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              className="text-green-600 hover:text-green-800"
                              title="Edit"
                            >
                              <FiEdit />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">
                {selected ? 'Edit Product' : 'Add Product'}
              </h2>
              <button type="button" onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 uppercase"
                  placeholder="e.g. Zovfolic"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    MRP (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Rate / Doctor Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Strip Size</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.stripSize}
                    onChange={(e) => setFormData({ ...formData, stripSize: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Strips / Box</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.stripsPerBox}
                    onChange={(e) => setFormData({ ...formData, stripsPerBox: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">GST %</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.gstPercent}
                    onChange={(e) => setFormData({ ...formData, gstPercent: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#1a365d] text-white rounded-lg hover:bg-[#152c4d] disabled:opacity-60"
                >
                  {saving ? 'Saving...' : selected ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && viewRow && (
        <div className="fixed inset-0 bg-black/40 z-[2000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">Product Details</h2>
              <button type="button" onClick={closeViewModal} className="text-slate-400 hover:text-slate-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 md:p-6 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">SNO</span>
                <span className="font-semibold text-slate-800">{viewRow.sno || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Product Name</span>
                <span className="font-semibold text-slate-800 uppercase">{viewRow.name}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">MRP</span>
                <span className="font-semibold text-slate-800">₹{formatMoney(viewRow.mrp)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Rate</span>
                <span className="font-semibold text-slate-800">₹{formatMoney(viewRow.rate)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Strip Size</span>
                <span className="font-semibold text-slate-800">{viewRow.stripSize}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Strips / Box</span>
                <span className="font-semibold text-slate-800">{viewRow.stripsPerBox}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">GST %</span>
                <span className="font-semibold text-slate-800">{viewRow.gstPercent ?? 5}%</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Created</span>
                <span className="font-semibold text-slate-800">{formatDate(viewRow.createdAt)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Updated</span>
                <span className="font-semibold text-slate-800">{formatDate(viewRow.updatedAt)}</span>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeViewModal}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  closeViewModal();
                  openEdit(viewRow);
                }}
                className="px-4 py-2 bg-[#1a365d] text-white rounded-lg hover:bg-[#152c4d]"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDB;
